/**
 * CustomizePopover — segmented controls for output language, detail level,
 * tone, and jurisdiction. Calls `onChange(key, value)` per click; resets
 * via `onReset()`.
 */
import React, { useEffect, useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';

const ROWS = [
  {
    key: 'language',
    label_ar: 'اللغة',          label_en: 'Language',
    options: [
      { value: 'arabic',    label_ar: 'عربي',     label_en: 'Arabic' },
      { value: 'english',   label_ar: 'English',  label_en: 'English' },
      { value: 'bilingual', label_ar: 'ثنائي',    label_en: 'Bilingual' },
    ],
  },
  {
    key: 'detail',
    label_ar: 'مستوى التفصيل',   label_en: 'Detail',
    options: [
      { value: 'summary',      label_ar: 'موجز',     label_en: 'Summary' },
      { value: 'detailed',     label_ar: 'تفصيلي',   label_en: 'Detailed' },
      { value: 'professional', label_ar: 'احترافي',  label_en: 'Pro' },
    ],
  },
  {
    key: 'tone',
    label_ar: 'النبرة',          label_en: 'Tone',
    options: [
      { value: 'formal',      label_ar: 'رسمية',  label_en: 'Formal' },
      { value: 'commercial',  label_ar: 'تجارية', label_en: 'Commercial' },
      { value: 'simplified',  label_ar: 'مبسطة',  label_en: 'Plain' },
    ],
  },
  {
    key: 'jurisdiction',
    label_ar: 'النطاق القانوني', label_en: 'Jurisdiction',
    options: [
      { value: 'saudi',   label_ar: 'السعودية', label_en: 'Saudi' },
      { value: 'bahrain', label_ar: 'البحرين',  label_en: 'Bahrain' },
      { value: 'uae',     label_ar: 'الإمارات', label_en: 'UAE' },
      { value: 'general', label_ar: 'عام',      label_en: 'General' },
    ],
  },
];

export function CustomizePopover({ settings, onChange, onReset, onClose, isArabic }) {
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    setTimeout(() => document.addEventListener('mousedown', onDoc), 10);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute z-50 mt-1"
      style={{
        background: '#FFFFFF',
        border: '1px solid #E8E8E8',
        borderRadius: '8px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
        width: '320px',
        padding: '8px 0',
        ...(isArabic ? { right: 0 } : { left: 0 }),
      }}
      data-testid="customize-popover"
    >
      {ROWS.map((row) => (
        <div
          key={row.key}
          className={cn('flex items-center justify-between gap-3 px-4', isArabic && 'flex-row-reverse')}
          style={{ padding: '10px 16px', borderBottom: '1px solid #F0F0F0' }}
        >
          <span className="font-arabic shrink-0" style={{ fontSize: '12px', color: '#666666' }}>
            {isArabic ? row.label_ar : row.label_en}
          </span>
          <div className="flex gap-0.5" style={{ background: '#F5F5F5', borderRadius: '6px', padding: '2px' }}>
            {row.options.map((opt) => {
              const active = settings[row.key] === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onChange(row.key, opt.value)}
                  className="font-arabic"
                  style={{
                    padding: '4px 10px',
                    fontSize: '11px',
                    borderRadius: '4px',
                    background: active ? '#111111' : 'transparent',
                    color: active ? '#FFFFFF' : '#666666',
                  }}
                  data-testid={`customize-${row.key}-${opt.value}`}
                >
                  {isArabic ? opt.label_ar : opt.label_en}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <div className="px-4 py-2 flex justify-end">
        <button
          type="button"
          onClick={onReset}
          className="font-arabic"
          style={{ fontSize: '11px', color: '#666666', cursor: 'pointer' }}
          data-testid="customize-reset"
        >
          {isArabic ? 'إعادة الضبط' : 'Reset'}
        </button>
      </div>
    </div>
  );
}

export default CustomizePopover;
